import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import isEmpty from "lodash/isEmpty";
import merge from "lodash/merge";
import omit from "lodash/omit";
import PropTypes from "prop-types";
import { Button, CoverModalButton, Form, FormControl, Modal, Spinner } from "@dpdgroupuk/mydpd-ui";
import { DATA_TYPES, DEFAULT_DATA, DEFAULT_SEARCH_QUERY, GENERAL_ERROR_CONFIG, HELP_INFO, NO_DATA_ERROR_CONFIG } from "../../constants";
import DataList from "../DataList";
import Error from "../Error";
import "./styles.scss";

const CommodityModal = ({
  show,
  onClose,
  onSearch,
  onCommodityCodeSearch,
  onSubmit
}) => {
  const [inputValue, setInputValue] = useState();
  const [query, setQuery] = useState(DEFAULT_SEARCH_QUERY);
  const [isFieldFocused, setIsFieldFocused] = useState(false);
  const [data, setData] = useState(DEFAULT_DATA[DATA_TYPES.COMMODITY]);
  const [dataType, setDataType] = useState(DATA_TYPES.COMMODITY);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const loader = useRef(null);
  const promiseRef = useRef();
  const dataListRef = useRef();
  const abortIfPending = useCallback(() => {
    if (promiseRef.current && typeof promiseRef.current.abort === "function") {
      promiseRef.current.abort();
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [promiseRef.current]);
  useEffect(() => abortIfPending, // eslint-disable-next-line react-hooks/exhaustive-deps
  []);
  const handleChange = useCallback(value => {
    const newValue = value.toUpperCase();
    setInputValue(newValue);
  }, [setInputValue]);
  const handleFocus = useCallback(() => {
    setIsFieldFocused(true);
  }, [setIsFieldFocused]);
  const handleBlur = useCallback(() => {
    setIsFieldFocused(false);
  }, [setIsFieldFocused]);
  const searchWithStatusHandler = useCallback((search, params) => {
    abortIfPending();
    const searching = search(params);
    searching.finally(() => {
      promiseRef.current = null;
    });
    promiseRef.current = searching;
    return searching;
  }, [abortIfPending]);
  const handleSearch = useCallback(async () => {
    const newQuery = { ...DEFAULT_SEARCH_QUERY,
      searchString: inputValue,
      commodityCode: undefined
    };

    if (isFieldFocused) {
      handleBlur();
    }

    setIsLoading(true);
    setError(null);
    setData(DEFAULT_DATA[DATA_TYPES.COMMODITY]);
    setDataType(DATA_TYPES.COMMODITY);
    setQuery(newQuery);
    searchWithStatusHandler(onSearch, omit(newQuery, "commodityCode")).then(({
      data
    } = {}) => {
      if (data) {
        setData(data);

        if (isEmpty(data?.result?.commodities)) {
          setError(NO_DATA_ERROR_CONFIG);
        }
      }
    }).catch(e => {
      setError(GENERAL_ERROR_CONFIG);
    }).finally(() => {
      setIsLoading(false);
    });
  }, [isFieldFocused, setQuery, inputValue, handleBlur, onSearch, searchWithStatusHandler]);
  const handlePagination = useCallback(async () => {
    const onPaginate = dataType === DATA_TYPES.COMMODITY ? onSearch : onCommodityCodeSearch;
    const params = dataType === DATA_TYPES.COMMODITY ? omit(query, "commodityCode") : omit(query, "searchString");
    const setDataFn = dataType === DATA_TYPES.COMMODITY ? (prevData, data) => ({ ...prevData,
      result: { ...prevData?.result,
        levels: [...prevData?.result?.levels, ...(data?.result?.levels || [])],
        commodities: merge(prevData?.result?.commodities, data?.result?.commodities)
      }
    }) : (prevData, data) => ({ ...prevData,
      result: [...prevData?.result, ...(data?.result || [])]
    });
    setIsLoading(true);
    setError(null);
    searchWithStatusHandler(onPaginate, params).then(({
      data
    } = {}) => {
      if (data) {
        setData(prevData => setDataFn(prevData, data));
      }
    }).catch(e => {
      setError(GENERAL_ERROR_CONFIG);
    }).finally(() => {
      setIsLoading(false);
    });
  }, [setIsLoading, onSearch, query, onCommodityCodeSearch, setData, dataType, searchWithStatusHandler]);
  const handleCommodityCodeClick = useCallback(async commodityCode => {
    const newQuery = { ...DEFAULT_SEARCH_QUERY,
      commodityCode
    };

    if (isFieldFocused) {
      handleBlur();
    }

    setIsLoading(true);
    setError(null);
    setData(DEFAULT_DATA[DATA_TYPES.COMMODITY_BY_CODE]);
    setDataType(DATA_TYPES.COMMODITY_BY_CODE);
    setQuery(newQuery);
    searchWithStatusHandler(onCommodityCodeSearch, omit(newQuery, "searchString")).then(({
      data
    } = {}) => {
      if (data) {
        setData(data);

        if (isEmpty(data?.result)) {
          setError(NO_DATA_ERROR_CONFIG);
        }
      }
    }).catch(e => {
      setError(GENERAL_ERROR_CONFIG);
      setDataType(DATA_TYPES.COMMODITY);
      setData(DEFAULT_DATA[DATA_TYPES.COMMODITY]);
    }).finally(() => {
      setIsLoading(false);
    });
  }, [setData, setDataType, setQuery, onCommodityCodeSearch, isFieldFocused, handleBlur, searchWithStatusHandler]);
  const isSearchButtonDisabled = useMemo(() => isLoading || !inputValue, [isLoading, inputValue]);
  const handleKeyPress = useCallback(event => {
    if (event.key === "Enter") {
      event.preventDefault();

      if (!isSearchButtonDisabled) {
        handleSearch();
      }
    }
  }, [handleSearch, isSearchButtonDisabled]);
  const handleSubmit = useCallback(code => {
    onSubmit(code);
    onClose();
  }, [onSubmit, onClose]);
  const handleObserver = useCallback(entries => {
    const target = entries[0];

    if (target.isIntersecting && !isLoading) {
      setQuery(query => ({ ...query,
        searchPage: query?.searchPage + 1
      }));
    }
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  [loader.current, isLoading]);
  useEffect(() => {
    const option = {
      root: null,
      rootMargin: "0px",
      threshold: 0
    }; // eslint-disable-next-line no-undef

    const observer = new IntersectionObserver(handleObserver, option);
    if (loader.current) observer.observe(loader.current);
  }, [handleObserver]);
  useEffect(() => {
    if (query?.searchPage !== 1) {
      handlePagination(query);
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [query?.searchPage]);
  const isDataListAvailable = useMemo(() => dataType === DATA_TYPES.COMMODITY_BY_CODE && !!data?.result?.length || dataType === DATA_TYPES.COMMODITY && !!data?.result?.levels?.length, [data?.result, dataType]);
  const isInfiniteScrollAvailable = useMemo(() => query?.searchPage * query?.searchPageSize < data?.totalResults && !isLoading, [query?.searchPage, query?.searchPageSize, data?.totalResults, isLoading]);
  useEffect(() => {
    if (isDataListAvailable) {
      dataListRef.current.scroll({
        top: 0,
        behavior: "smooth"
      });
    }
  }, [isDataListAvailable]);
  return /*#__PURE__*/React.createElement(Modal, {
    show: show,
    dialogClassName: "mydpd-commodity-finder-modal",
    contentClassName: "mydpd-commodity-finder-modal-content",
    onHide: onClose
  }, /*#__PURE__*/React.createElement(Modal.Header, {
    onHide: onClose,
    closeButton: true
  }, "Commodity Code Finder", /*#__PURE__*/React.createElement(CoverModalButton, {
    items: HELP_INFO,
    title: "Commodity Code Finder"
  })), /*#__PURE__*/React.createElement(Modal.Body, null, /*#__PURE__*/React.createElement(Form, {
    className: "d-flex align-items-center mydpd-commodity-search-form"
  }, /*#__PURE__*/React.createElement(FormControl.Input, {
    label: "Search Term",
    name: "searchString",
    maxLength: 255,
    onKeyPress: handleKeyPress,
    value: inputValue,
    onChange: handleChange,
    classes: {
      group: "mydpd-commodity-search-form-group"
    },
    helperText: "E.g. Leather Shoes, Toys, Women\u2019s Blouse",
    forceLabel: false,
    meta: {
      active: isFieldFocused
    },
    onFocus: handleFocus,
    onBlur: handleBlur
  }), /*#__PURE__*/React.createElement(Button, {
    variant: "dark",
    onClick: handleSearch,
    disabled: isSearchButtonDisabled,
    className: "mydpd-commodity-search-form-button"
  }, "Search")), isDataListAvailable ? /*#__PURE__*/React.createElement("div", {
    className: "mydpd-commodity-finder-data-list-container",
    ref: dataListRef
  }, /*#__PURE__*/React.createElement(DataList, {
    data: data.result,
    onCommodityCodeClick: handleCommodityCodeClick,
    onSubmit: handleSubmit,
    dataType: dataType
  }), isInfiniteScrollAvailable && /*#__PURE__*/React.createElement("div", {
    ref: loader
  }), isLoading && /*#__PURE__*/React.createElement(Spinner, null)) : isLoading && /*#__PURE__*/React.createElement("div", {
    className: "mydpd-commodity-finder-spinner-container"
  }, /*#__PURE__*/React.createElement(Spinner, null)), error && /*#__PURE__*/React.createElement(Error, {
    error: error
  })));
};

CommodityModal.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func,
  onSearch: PropTypes.func,
  onCommodityCodeSearch: PropTypes.func,
  onSubmit: PropTypes.func
};
export default CommodityModal;